/**
 * Shared product positioning copy — AI Preparation Layer for Education.
 * Keep claims careful: no compliance guarantees.
 */

export const PRODUCT_POSITIONING_PHRASE = 'The AI Preparation Layer for Education';

export const HERO_HEADLINE =
  'Prepare Educational Documents for AI — Without Sharing PII';

export const HERO_HEADLINE_ACCENT = 'Without Sharing PII';

export const HERO_SUPPORT =
  'Redact AI helps educators remove personally identifiable information from educational documents before using them with AI tools, sharing them for training, or collaborating with others.';

export const PREPARATION_FLOW = [
  {
    step: 1,
    title: 'Upload',
    description:
      'Upload any educational document such as IEPs, evaluation reports, FIEs, progress reports, and more.',
  },
  {
    step: 2,
    title: 'Prepare',
    description:
      'Redact AI identifies and removes personally identifiable information while preserving the integrity of your document.',
  },
  {
    step: 3,
    title: 'Use',
    description:
      'Download your prepared document and review it before using it with AI tools, sharing it for training, or collaborating with others.',
  },
] as const;

export const HERO_STATUS_CYCLE = [
  'Scanning document…',
  'Detecting PII…',
  'Preparing document…',
  'Verifying page integrity…',
  'Ready for Review',
] as const;

export const HERO_SECURITY_NOTE =
  'Your documents are processed securely. Redact AI does not use them to train AI models.';

export const HERO_REVIEW_BANNER =
  'Review the prepared document before uploading it to an AI platform or sharing it.';

export const FEATURE_PILLARS = [
  {
    title: 'AI Preparation',
    description:
      'Prepare educational documents before uploading them into generative AI tools.',
  },
  {
    title: 'PII Detection',
    description:
      'Identify common personally identifiable information within educational records.',
  },
  {
    title: 'Document Integrity',
    description:
      'Preserve the original document structure and page count during preparation.',
  },
  {
    title: 'Educator Workflow',
    description:
      'Designed around documents used by teachers, evaluators, special educators, related-service providers, and consultants.',
  },
  {
    title: 'Private Preparation',
    description:
      'Create a de-identified working copy rather than modifying the original record.',
  },
  {
    title: 'Professional Learning',
    description:
      'Prepare real-world examples for trainings, demonstrations, case discussions, and courses when appropriate.',
  },
] as const;

export const USE_CASES = [
  {
    title: 'Evaluation Teams',
    description:
      'Prepare evaluation reports or case materials before AI-assisted analysis or professional discussion.',
  },
  {
    title: 'Special Education Professionals',
    description:
      'Prepare IEPs, service records, and instructional documents before appropriate AI-supported workflows.',
  },
  {
    title: 'Professional Development',
    description:
      'Create de-identified examples for trainings, workshops, university courses, and demonstrations.',
  },
  {
    title: 'Educational Consultants',
    description:
      'Prepare client documents before using AI-assisted drafting, analysis, or workflow tools.',
  },
  {
    title: 'University Faculty',
    description:
      'Prepare educational case documents before sharing them with students for instructional purposes.',
  },
] as const;

/** Optional pre-process category — analytics only; never required. */
export const DOCUMENT_PREPARE_CATEGORIES = [
  { id: '', label: 'Select type (optional)' },
  { id: 'iep', label: 'IEP' },
  { id: 'evaluation_fie', label: 'Evaluation / FIE' },
  { id: 'progress_report', label: 'Progress Report' },
  { id: 'behavior_intervention', label: 'Behavior / Intervention Document' },
  { id: 'service_documentation', label: 'Service Documentation' },
  { id: 'instructional_material', label: 'Instructional Material' },
  { id: 'pd_training', label: 'Professional Development / Training' },
  { id: 'other', label: 'Other' },
] as const;

export type DocumentPrepareCategoryId =
  (typeof DOCUMENT_PREPARE_CATEGORIES)[number]['id'];

export const REVIEW_REMINDER =
  'Redact AI can help identify and remove sensitive information, but automated detection may not identify everything. Review the prepared document before using it with an AI system or sharing it.';

export const AI_PREPARATION_STATUS_READY_FOR_REVIEW = 'Ready for review';

export const PILOT_CONTRIBUTION_NOTE =
  "You're helping us improve the AI Preparation Layer for Education.";

/** Lightweight feedback — no document contents. Opens mailto. */
export const PILOT_FEEDBACK_MAILTO =
  'mailto:support@thelearningindex.com?subject=Redact%20AI%20Pilot%20Feedback&body=Did%20Redact%20AI%20prepare%20the%20entire%20document%20correctly%3F%0A%0ADid%20you%20notice%20any%20missed%20sensitive%20information%3F%0A%0AWhat%20type%20of%20workflow%20are%20you%20preparing%20this%20document%20for%3F%0A%0A(Please%20do%20not%20include%20student%20names%20or%20document%20contents.)';
